<template>
  <div class="agents-flow-container" tabindex="0" @keydown="handleKeyDown">
    
    <div class="floating-menu flex items-center">
        <select class="select select-bordered select-sm mr-2" v-model="selectedAgentToAdd">
            <option disabled value="">Select an Agent to add</option>
            <option v-for="agent in orgAgents" :key="agent.agentid || agent.agentId" :value="agent">{{ agent.agentname || agent.agentName }}</option>
        </select>
        <button @click="addAgentNode" class="btn btn-sm mr-2" :disabled="selectedAgentToAdd === ''">Add Agent</button>
        <button v-if="isElementSelected === true" @click="requestDeleteSelectedElements" class="btn btn-error btn-sm">
             <Icon icon="tabler:trash" class="mr-1" width="16" height="16" /> Delete
        </button>
    </div>

    <div class="floating-flow-controls">
        <label class="label router-model-label mb-2">
          <span class="label-text">{{ routerLlmLabel }}</span>
        </label>
        <LLMSelector
          class="router-model-selector"
          :availableLLMModels="availableLLMModels"
          :selectedLLMModel="selectedRouterLlmModel"
          @update:selectedLLMModel="emit('update:selectedRouterLlmModel', $event)"
        />
        <div class="floating-flow-controls__mode-row">
          <AgentConnectionModeSelector v-model="connectionMode" class="floating-flow-controls__mode-selector" />
          <button @click="organizeFlow" class="btn btn-sm floating-flow-controls__organize-btn">{{ organizeFlowLabel }}</button>
        </div>
        <label class="label cursor-pointer mt-3" :dir="isRtl === true ? 'rtl' : 'ltr'">
          <span class="label-text">{{ showSubAgentReportsLabel }}</span>
          <input
            type="checkbox"
            class="toggle toggle-sm ml-3"
            :checked="showSubAgentConversationsInReports === true"
            @change="emit('update:showSubAgentConversationsInReports', $event.target.checked === true)"
          />
        </label>
    </div>

    <VueFlow 
      :key="flowKey" 
      v-model="elements" 
      :fit-view-on-init="true"
      :min-zoom="0.1"
      :max-zoom="8"
      :default-zoom="1"
      @edge-double-click="handleEdgeDoubleClick" 
      @edge-click="handleEdgeClick"
      :select-nodes-on-drag="true"
      :zoom-on-scroll="true"
      :zoom-on-pinch="true"
      :zoom-on-double-click="true"
      :pan-on-drag="true"
      :delete-key-code="null" 
      class="llm-flow-canvas"
    >
      <Background />
      <Controls>
         <ControlButton title="Delete Selected" @click="requestDeleteSelectedElements">
            <Icon icon="tabler:trash" width="16" height="16" />
        </ControlButton>
      </Controls>

      <template #node-agent="props">
        <div
          class="agent-flow-node vue-flow__node-default"
          :class="{ 'agent-flow-node--root': props.data.isRoot === true }"
        >
          <Handle type="target" :position="Position.Top" v-if="props.data.isRoot !== true" />
          <div class="agent-flow-node__header">
            <span
              class="agent-flow-node__badge"
              :class="{ 'agent-flow-node__badge--rtl': isRtlText(props.data.label) === true }"
            >
              {{ getNodeBadgeText(props.data.isRoot === true, props.data.label) }}
            </span>
          </div>
          <div class="agent-flow-node__body">
            <div
              class="agent-flow-node__identity"
              :class="{ 'agent-flow-node__identity--rtl': isRtlText(props.data.label) === true }"
              :dir="isRtlText(props.data.label) === true ? 'rtl' : 'ltr'"
            >
              <div
                v-if="agentLogos[props.data.agentId] !== undefined"
                class="agent-flow-node__avatar"
              >
                <img
                  :src="agentLogos[props.data.agentId]"
                  class="agent-flow-node__avatar-image"
                  alt=""
                />
              </div>
              <div class="agent-flow-node__text">
                <div class="agent-flow-node__name" :title="props.data.label">{{ props.data.label }}</div>
                <div class="agent-flow-node__meta">
                  {{ getNodeMetaText(props.data.isRoot === true, props.data.label) }}
                </div>
              </div>
            </div>
          </div>
          <Handle type="source" :position="Position.Bottom" />
        </div>
      </template>

      <template #edge-smoothstep="props">
        <SmoothStepEdge 
           :key="getEdgeLabelKey(props)" 
           :id="props.id" 
           :source-x="props.sourceX" 
           :source-y="props.sourceY" 
           :target-x="props.targetX" 
           :target-y="props.targetY" 
           :source-position="props.sourcePosition" 
           :target-position="props.targetPosition" 
           :label="props.label" 
           :markerEnd="getEdgeMarkerEnd(props)"
           :style="getEdgePathStyle(props)"
        />
        
        <EdgeLabelRenderer>
          <div
            :key="getEdgeLabelKey(props)"
            :style="{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${(props.sourceX + props.targetX) / 2}px,${(props.sourceY + props.targetY) / 2}px)`,
              pointerEvents: 'all',
              zIndex: 20
            }"
            :class="getEdgeRouteCardClass(props)"
            :dir="isRtlText(props.label) === true || isRtl === true ? 'rtl' : 'ltr'"
            @click.stop="handleEdgeClick({ edge: props, event: $event })"
            @dblclick.stop="handleEdgeDoubleClick({ edge: props, event: $event })"
          >
            <div class="edge-route-card__mode">{{ getEdgeModeLabel(props.data) }}</div>
            <div class="edge-route-card__condition">
              {{ getEdgeConditionText(props.label) }}
            </div>
          </div>
        </EdgeLabelRenderer>
      </template>

    </VueFlow>

    <EdgeConditionModal
        :is-open="isEdgeModalOpen !== undefined && isEdgeModalOpen !== null && isEdgeModalOpen === true" 
        :condition="currentEdgeCondition"
        @close="closeEdgeModal"
        @save="saveEdgeCondition"
        @delete-edge="handleDeleteEdgeFromModal"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick, defineExpose } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { VueFlow, useVueFlow, SmoothStepEdge, EdgeLabelRenderer, Handle, Position, MarkerType } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls, ControlButton } from '@vue-flow/controls';
import EdgeConditionModal from './EdgeConditionModal.vue';
import AgentConnectionModeSelector from './AgentConnectionModeSelector.vue';
import LLMSelector from './LLMSelector.vue';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import { useI18n } from '@/i18n';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const props = defineProps({
  currentAgentId: {
    type: String,
    required: true
  },
  currentAgentName: {
    type: String,
    required: true
  },
  availableLLMModels: {
    type: Array,
    default: () => []
  },
  selectedRouterLlmModel: {
    type: String,
    default: null
  },
  showSubAgentConversationsInReports: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:selectedRouterLlmModel', 'update:showSubAgentConversationsInReports']);
const { 
  addNodes, 
  onConnect, 
  addEdges, 
  removeNodes, 
  removeEdges, 
  getSelectedNodes, 
  getSelectedEdges,
  findEdge,     
  setNodes,
  setEdges,
  fitView,
  findNode,
  addSelectedEdges,
  removeSelectedNodes
} = useVueFlow();

const { currentLanguage } = useI18n();
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);
const routerLlmLabel = computed(() => isRtl.value === true ? 'מודל LLM לניתוב' : 'Router LLM Model');
const organizeFlowLabel = computed(() => isRtl.value === true ? 'סדר זרימה' : 'Organize Flow');
const showSubAgentReportsLabel = computed(() => isRtl.value === true ? 'הצגת שיחות של סוכני משנה בדוחות' : 'Show sub-agent conversations in reports');

let id = 0;
const elements = ref([]); 
const orgAgents = ref([]);
const agentLogos = ref({});
const selectedAgentToAdd = ref('');

const isEdgeModalOpen = ref(false);
const editingEdgeId = ref(null);
const currentEdgeCondition = ref('');
const currentConnectionMode = ref('fullmove');
const flowKey = ref(0);

const isElementSelected = computed(() => {
    return getSelectedNodes.value.length > 0 || getSelectedEdges.value.length > 0;
});

function normalizeConnectionMode(mode) {
    return mode === 'questions' ? 'questions' : 'fullmove';
}

function isRtlText(value) {
    if (typeof value !== 'string') {
        return false;
    }
    return /[\u0590-\u05FF\u0600-\u06FF]/.test(value) === true;
}

function hasEdgeCondition(labelValue) {
    return typeof labelValue === 'string' && labelValue.trim().length > 0;
}

function getEdgeLabelKey(edgeProps) {
    const edgeId = typeof edgeProps?.id === 'string' ? edgeProps.id : 'edge';
    const labelText = hasEdgeCondition(edgeProps?.label) === true ? edgeProps.label.trim() : 'empty';
    const modeText = normalizeConnectionMode(edgeProps?.data?.mode);
    return `${edgeId}_${modeText}_${labelText}`;
}

function getEdgeModeLabel(edgeData) {
    const mode = normalizeConnectionMode(edgeData?.mode);
    if (mode === 'questions') {
        if (isRtl.value === true) {
            return 'שאלות';
        }
        return 'Questions';
    }
    if (isRtl.value === true) {
        return 'העברה מלאה';
    }
    return 'Full move';
}

function getEdgeModeClass(edgeData) {
    if (normalizeConnectionMode(edgeData?.mode) === 'questions') {
        return 'edge-route-card--questions';
    }
    return 'edge-route-card--fullmove';
}

function isEdgeSelected(edgeProps) {
    return edgeProps?.selected === true;
}

function getEdgePathStyle(edgeProps) {
    const baseStyle = edgeProps?.style !== undefined && edgeProps?.style !== null ? edgeProps.style : {};
    if (isEdgeSelected(edgeProps) === true) {
        return { ...baseStyle, stroke: '#2563eb', strokeWidth: 3 };
    }
    return baseStyle;
}

function getEdgeMarkerEnd(edgeProps) {
    const markerEnd = edgeProps?.markerEnd;
    const color = isEdgeSelected(edgeProps) === true ? '#2563eb' : '#94a3b8';
    if (markerEnd !== undefined && markerEnd !== null && typeof markerEnd === 'object') {
        return { ...markerEnd, color };
    }
    return { type: MarkerType.ArrowClosed, width: 30, height: 30, color };
}

function getEdgeRouteCardClass(edgeProps) {
    const classes = ['edge-route-card', getEdgeModeClass(edgeProps?.data)];
    if (hasEdgeCondition(edgeProps?.label) !== true) {
        classes.push('edge-route-card--empty');
    }
    if (isEdgeSelected(edgeProps) === true) {
        classes.push('edge-route-card--selected');
    }
    return classes;
}

function handleEdgeClick({ edge, event }) {
    if (edge === undefined || edge === null) {
        return;
    }
    if (event !== undefined && event !== null && typeof event.stopPropagation === 'function') {
        event.stopPropagation();
    }
    removeSelectedNodes(getSelectedNodes.value);
    addSelectedEdges([edge]);
}

function getEdgeConditionText(labelValue) {
    if (hasEdgeCondition(labelValue) === true) {
        return labelValue.trim();
    }
    if (isRtl.value === true) {
        return 'לחצו פעמיים כדי להגדיר מתי לנתב';
    }
    return 'Double click to set when to route';
}

function getNodeBadgeText(isRoot, labelValue) {
    const labelIsRtl = isRtlText(labelValue) === true;
    if (isRoot === true) {
        if (labelIsRtl === true || isRtl.value === true) {
            return 'סוכן נוכחי';
        }
        return 'Current Agent';
    }
    if (labelIsRtl === true || isRtl.value === true) {
        return 'סוכן מחובר';
    }
    return 'Connected Agent';
}

function getNodeMetaText(isRoot, labelValue) {
    const labelIsRtl = isRtlText(labelValue) === true;
    if (isRoot === true) {
        if (labelIsRtl === true || isRtl.value === true) {
            return 'מנתב הודעות לסוכנים מתמחים';
        }
        return 'Routes incoming messages to specialists';
    }
    if (labelIsRtl === true || isRtl.value === true) {
        return 'עונה כשתנאי הניתוב מתאים';
    }
    return 'Answers when its route condition matches';
}

function isEdgeElement(elementValue) {
    return elementValue !== undefined && elementValue !== null && elementValue.source !== undefined && elementValue.target !== undefined;
}

function updateEdgesMode(edgeIds, mode) {
    const normalizedMode = normalizeConnectionMode(mode);
    const ids = Array.isArray(edgeIds) === true ? edgeIds : [];
    elements.value = elements.value.map(el => {
        if (isEdgeElement(el) === true && (ids.length === 0 || ids.includes(el.id) === true)) {
            const currentData = el.data !== undefined && el.data !== null ? el.data : {};
            return { ...el, data: { ...currentData, mode: normalizedMode } };
        }
        return el;
    });
    const selectedEdges = getSelectedEdges.value;
    for (let i = 0; i < selectedEdges.length; i = i + 1) {
        const edge = selectedEdges[i];
        if (edge !== undefined && edge !== null && (ids.length === 0 || ids.includes(edge.id) === true)) {
            if (edge.data === undefined || edge.data === null) {
                edge.data = {};
            }
            edge.data.mode = normalizedMode;
        }
    }
}

const connectionMode = computed({
    get() {
        const selectedEdges = getSelectedEdges.value;
        if (selectedEdges.length > 0) {
            const firstEdge = selectedEdges[0];
            if (firstEdge !== undefined && firstEdge !== null && firstEdge.data !== undefined && firstEdge.data !== null && firstEdge.data.mode === 'questions') {
                return 'questions';
            }
        }
        return currentConnectionMode.value;
    },
    set(mode) {
        const normalizedMode = normalizeConnectionMode(mode);
        currentConnectionMode.value = normalizedMode;
        const selectedEdges = getSelectedEdges.value;
        if (selectedEdges.length > 0) {
            updateEdgesMode(selectedEdges.map(edge => edge.id), normalizedMode);
            return;
        }
        const existingEdges = elements.value.filter(el => isEdgeElement(el) === true);
        if (existingEdges.length === 1) {
            updateEdgesMode([existingEdges[0].id], normalizedMode);
        }
    }
});

function getNextId() {
  return `agent-node-${id++}`;
}

async function fetchOrgAgents() {
  try {
    const data = await getAllAIChats({ includeStyles: true });
    orgAgents.value = data.filter(a => a.agentid !== props.currentAgentId);
    
    const logos = {};
    for (let i = 0; i < data.length; i = i + 1) {
        const a = data[i];
        const style = a.agentStyle === undefined ? a.agentstyle : a.agentStyle;
        if (style !== undefined && style !== null) {
            const logoBase64 = style.logoBase64 !== undefined && style.logoBase64 !== null && typeof style.logoBase64 === 'string' ? style.logoBase64 : (style.logobase64 !== undefined && style.logobase64 !== null && typeof style.logobase64 === 'string' ? style.logobase64 : '');
            if (logoBase64 !== '') {
                logos[a.agentid] = logoBase64.startsWith('data:image') === true ? logoBase64 : `data:image/png;base64,${logoBase64}`;
            }
        }
    }
    agentLogos.value = logos;
  } catch (error) {
    console.error('Failed to load org agents', error);
  }
}

function initRootNode() {
  const rootNodeExists = elements.value.some(el => el.data !== undefined && el.data !== null && el.data.isRoot === true);
  if (rootNodeExists === false) {
    const rootNode = {
      id: props.currentAgentId,
      type: 'agent', 
      position: { x: 50, y: 250 },
      label: props.currentAgentName,
      data: {
        label: props.currentAgentName,
        agentId: props.currentAgentId,
        isRoot: true,
      },
      draggable: true,
      connectable: true, 
      deletable: false,
    };
    addNodes([rootNode]);
  }
}

function shouldReplaceRootEndpoint(endpointValue, oldRootId, oldRootAgentId) {
  const endpoint = typeof endpointValue === 'string' ? endpointValue : '';
  const currentRootId = typeof oldRootId === 'string' ? oldRootId : '';
  const currentRootAgentId = typeof oldRootAgentId === 'string' ? oldRootAgentId : '';
  if (endpoint === '') {
    return false;
  }
  if (endpoint === currentRootId || endpoint === currentRootAgentId || endpoint === 'new-agent') {
    return true;
  }
  return false;
}

function syncRootNodeId(nextRootId) {
  if (typeof nextRootId !== 'string' || nextRootId === '') {
    return;
  }
  const rootNode = elements.value.find(el => el.data !== undefined && el.data !== null && el.data.isRoot === true);
  if (rootNode === undefined || rootNode === null) {
    return;
  }
  const oldRootId = typeof rootNode.id === 'string' ? rootNode.id : '';
  const oldRootAgentId = rootNode.data !== undefined && rootNode.data !== null && typeof rootNode.data.agentId === 'string' ? rootNode.data.agentId : '';
  if (oldRootId === nextRootId && oldRootAgentId === nextRootId) {
    return;
  }
  elements.value = elements.value.map(el => {
    if (el.data !== undefined && el.data !== null && el.data.isRoot === true) {
      return {
        ...el,
        id: nextRootId,
        data: {
          ...el.data,
          agentId: nextRootId
        }
      };
    }
    if (el.source !== undefined && el.target !== undefined) {
      const nextSource = shouldReplaceRootEndpoint(el.source, oldRootId, oldRootAgentId) === true ? nextRootId : el.source;
      const nextTarget = shouldReplaceRootEndpoint(el.target, oldRootId, oldRootAgentId) === true ? nextRootId : el.target;
      if (nextSource !== el.source || nextTarget !== el.target) {
        return {
          ...el,
          source: nextSource,
          target: nextTarget
        };
      }
    }
    return el;
  });
}

       function addAgentNode() {
         if (selectedAgentToAdd.value === '') return;
         const agent = selectedAgentToAdd.value;
         const newNodeId = agent.agentid || agent.agentId;

         const nodeExists = elements.value.some(el => el.id === newNodeId);
         if (nodeExists === true) {
           selectedAgentToAdd.value = '';
           return;
         }

         const newNode = {
           id: newNodeId,
           type: 'agent',
           position: { x: Math.random() * 400 + 100, y: Math.random() * 200 + 50 },
           label: agent.agentname || agent.agentName,
           data: {
             label: agent.agentname || agent.agentName,
             agentId: newNodeId,
             isRoot: false
           },
           draggable: true,
         };
         addNodes([newNode]);
         selectedAgentToAdd.value = '';
       }

onConnect((params) => {
  const newEdge = { 
    id: `e-${params.source}-${params.target}-${new Date().getTime()}`, 
    ...params, 
    type: 'smoothstep',
    selectable: true,
    interactionWidth: 24,
    markerEnd: { type: MarkerType.ArrowClosed, width: 30, height: 30, color: '#94a3b8' },
    style: { stroke: '#94a3b8', strokeWidth: 2 },
    data: { ifCondition: '', mode: currentConnectionMode.value },
    label: '' 
  };
  addEdges([newEdge]);
});

function handleEdgeDoubleClick({ edge, event }) {
  if (edge === undefined || edge === null) return;
  event.stopPropagation(); 
  event.preventDefault();  
  editingEdgeId.value = edge.id;
  if (edge.data !== undefined && edge.data !== null && edge.data.ifCondition !== undefined && edge.data.ifCondition !== null && edge.data.ifCondition !== '') {
    currentEdgeCondition.value = edge.data.ifCondition;
  } else {
    currentEdgeCondition.value = '';
  }
  isEdgeModalOpen.value = true;
}

function closeEdgeModal() {
    isEdgeModalOpen.value = false;
    editingEdgeId.value = null;
    currentEdgeCondition.value = '';
}

function saveEdgeCondition(condition) {
    const resolvedCondition = typeof condition === 'string' ? condition : '';
    if (editingEdgeId.value !== null) {
        const edge = findEdge(editingEdgeId.value);
        if (edge !== undefined && edge !== null) {
            edge.label = resolvedCondition;
            if (edge.data === undefined || edge.data === null) {
                edge.data = {}
            }
            edge.data.ifCondition = resolvedCondition;
            
            elements.value = elements.value.map(el => {
                if (el.id === editingEdgeId.value && el.source !== undefined && el.target !== undefined) {
                    const newData = el.data !== undefined && el.data !== null ? { ...el.data, ifCondition: resolvedCondition } : { ifCondition: resolvedCondition }
                    return { ...el, label: resolvedCondition, data: newData }
                }
                return el;
            });
        }
    }
    closeEdgeModal();
}

function handleDeleteEdgeFromModal() {
    if (editingEdgeId.value !== null) {
        removeEdges([editingEdgeId.value]);
    }
    closeEdgeModal();
}

function requestDeleteSelectedElements() {
  const selectedNodes = getSelectedNodes.value.filter(node => node.data?.isRoot !== true); 
  const selectedEdges = getSelectedEdges.value;

  if (selectedNodes.length > 0) {
    removeNodes(selectedNodes.map(n => n.id));
  }
  if (selectedEdges.length > 0) {
    removeEdges(selectedEdges.map(e => e.id));
  }
}

function handleKeyDown(event) {
  const tagName = event.target.tagName.toLowerCase();
  if (isEdgeModalOpen.value === true) return;
  if (tagName === 'input' || tagName === 'textarea') return;
  if (event.key === 'Delete' || event.key === 'Backspace') {
    requestDeleteSelectedElements();
  }
}

function getFlowData() {
    return elements.value.map(el => {
        if (el.position !== undefined && el.position !== null) {
            return {
                id: el.id,
                type: el.type, 
                position: el.position,
                label: el.data.label, 
                data: { ...el.data },
                draggable: el.draggable,
            };
        } else {
            const edgeData = el.data !== undefined && el.data !== null ? el.data : {};
            const resolvedMode = normalizeConnectionMode(edgeData.mode !== undefined ? edgeData.mode : currentConnectionMode.value);
            return {
                id: el.id,
                source: el.source,
                target: el.target,
                type: el.type,
                label: el.label,
                data: { ...edgeData, mode: resolvedMode }
            };
        }
    });
}

function loadFlowData(flowData) {
  if (flowData === undefined || flowData === null || Array.isArray(flowData) === false) {
    elements.value = [];
    initRootNode();
    nextTick(() => fitView({ maxZoom: 1 }));
    return;
  }

  const nodesArr = [];
  const edgesArr = [];
  let maxNumericId = 0;

  for (let i = 0; i < flowData.length; i = i + 1) {
    const el = flowData[i];

    if (el.source !== undefined && el.target !== undefined) {
      const existingData = el.data !== undefined && el.data !== null ? el.data : {};
      const normalizedMode = normalizeConnectionMode(existingData.mode);
      edgesArr.push({
        ...el,
        selectable: true,
        interactionWidth: 24,
        data: { ...existingData, mode: normalizedMode },
        type: (el.type !== undefined && el.type !== null && el.type !== '') ? el.type : 'smoothstep',
        markerEnd: (el.markerEnd !== undefined && el.markerEnd !== null) ? el.markerEnd : { type: MarkerType.ArrowClosed, width: 30, height: 30, color: '#94a3b8' },
        style: (el.style !== undefined && el.style !== null) ? el.style : { stroke: '#94a3b8', strokeWidth: 2 },
      });
      continue;
    }

    if (el.position !== undefined && el.position !== null) {
      nodesArr.push(el);
      if (typeof el.id === 'string' && el.id.startsWith('agent-node-') === true) {
        const numPart = parseInt(el.id.split('-')[2], 10);
        if (isNaN(numPart) === false && numPart > maxNumericId) {
          maxNumericId = numPart;
        }
      }
    }
  }

  id = maxNumericId + 1;
  elements.value = [...nodesArr, ...edgesArr];
  const firstEdgeWithMode = edgesArr.find(edge => edge !== undefined && edge !== null && edge.data !== undefined && edge.data !== null && edge.data.mode === 'questions');
  currentConnectionMode.value = firstEdgeWithMode !== undefined && firstEdgeWithMode !== null ? 'questions' : 'fullmove';
  syncRootNodeId(props.currentAgentId);
  const normalizedNodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
  const normalizedEdges = elements.value.filter(el => el.source !== undefined && el.target !== undefined);
  flowKey.value++;

  nextTick(() => {
    setNodes(normalizedNodes);
    setEdges(normalizedEdges);
    nextTick(() => fitView({ maxZoom: 1 }));
  });
}

function organizeFlow() {
    const nodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
    const edges = elements.value.filter(el => el.source !== undefined && el.source !== null);

    const rootNode = nodes.find(n => n.data !== undefined && n.data !== null && n.data.isRoot === true);
    if (rootNode === undefined || rootNode === null) return;

    const adj = new Map();
    for (let i = 0; i < nodes.length; i = i + 1) {
        adj.set(nodes[i].id, []);
    }
    for (let i = 0; i < edges.length; i = i + 1) {
        const edge = edges[i];
        if (adj.has(edge.source) === true) {
            adj.get(edge.source).push(edge.target);
        }
    }

           const ySpacing = 180;
           const xSpacing = 280;
           const nodeWidthEstimate = 300;

           const newPositions = new Map();
           const levels = new Map();
           const nodesAtLevel = new Map();

           const queue = [{ nodeId: rootNode.id, level: 0 }];
           const visited = new Set([rootNode.id]);
           levels.set(rootNode.id, 0);

           if (nodesAtLevel.has(0) === false) {
               nodesAtLevel.set(0, []);
           }
           nodesAtLevel.get(0).push(rootNode.id);

           let head = 0;
           while(head < queue.length) {
               const item = queue[head];
               head = head + 1;
               const nodeId = item.nodeId;
               const level = item.level;
               const children = adj.get(nodeId) || [];
               for (let i = 0; i < children.length; i = i + 1) {
                   const childId = children[i];
                   const childNode = findNode(childId);
                   if (childNode !== undefined && childNode !== null && visited.has(childId) === false) {
                       visited.add(childId);
                       levels.set(childId, level + 1);
                       if (nodesAtLevel.has(level + 1) === false) {
                           nodesAtLevel.set(level + 1, []);
                       }
                       nodesAtLevel.get(level + 1).push(childId);
                       queue.push({ nodeId: childId, level: level + 1 });
                   }
               }
           }

           const canvasElement = document.querySelector('.llm-flow-canvas');
           const canvasCenterX = canvasElement !== null ? canvasElement.clientWidth / 2 : 400;

           nodesAtLevel.forEach((nodesInLevel, level) => {
               const y = 50 + level * ySpacing;
               const numNodes = nodesInLevel.length;
               const levelWidth = numNodes * nodeWidthEstimate + Math.max(0, numNodes - 1) * xSpacing;
               let currentX = canvasCenterX - levelWidth / 2;

               for (let i = 0; i < nodesInLevel.length; i = i + 1) {
                   const nodeId = nodesInLevel[i];
                   newPositions.set(nodeId, { x: currentX + nodeWidthEstimate / 2, y });
                   currentX = currentX + nodeWidthEstimate + xSpacing;
               }
           });

    elements.value = elements.value.map(el => {
        if (el.position !== undefined && el.position !== null && newPositions.has(el.id) === true) {
            return { ...el, position: newPositions.get(el.id) };
        }
        return el;
    });

    nextTick(() => {
        fitView({ maxZoom: 1 });
    });
}

defineExpose({
  getFlowData,
  loadFlowData,
  fitView
});

onMounted(() => {
  fetchOrgAgents();
  initRootNode(); 
  setTimeout(() => fitView({ maxZoom: 1 }), 100);
});

watch(() => props.currentAgentId, (newId) => {
    if (newId !== undefined && newId !== null && newId !== '') {
        const rootNode = elements.value.find(el => el.data !== undefined && el.data !== null && el.data.isRoot === true);
        if (rootNode !== undefined && rootNode !== null) {
            syncRootNodeId(newId);
        } else if (rootNode === undefined || rootNode === null) {
            initRootNode();
        }
    }
});

watch(() => props.currentAgentName, (newName) => {
    if (newName !== undefined && newName !== null && newName !== '') {
        const rootNode = elements.value.find(el => el.data !== undefined && el.data !== null && el.data.isRoot === true);
        if (rootNode !== undefined && rootNode !== null) {
            rootNode.label = newName;
            rootNode.data.label = newName;
        }
    }
});
</script>

<style scoped>
.agents-flow-container {
  height: 60vh;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
}

.floating-menu {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.96);
  padding: 10px 12px;
  border-radius: 12px;
  display: flex;
  border: 1px solid #e2e8f0;
}

.floating-flow-controls {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 30;
  width: 340px;
  background-color: rgba(255, 255, 255, 0.96);
  padding: 16px 12px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.floating-flow-controls__mode-row {
  display: flex;
  align-items: stretch;
  margin-top: 12px;
}

.floating-flow-controls__organize-btn {
  flex-shrink: 0;
  margin-inline-start: 8px;
  align-self: stretch;
}

.floating-flow-controls__mode-selector {
  flex: 1;
  min-width: 0;
}

.router-model-label {
  padding-top: 4px;
  padding-bottom: 0;
}

.router-model-selector {
  margin-bottom: 0;
}

.router-model-selector :deep(.llm-selector-dropdown-container) {
  margin-bottom: 0;
}

.floating-flow-controls__mode-selector :deep(.agent-connection-mode-selector-dropdown-container) {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

.llm-flow-canvas {
  flex-grow: 1;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
  background-color: #f8fafc;
  overflow: hidden;
}

.edge-route-card {
  min-width: 150px;
  max-width: 240px;
  background: #ffffff;
  padding: 8px 10px;
  border-radius: 14px;
  border: 1px solid #dbe3ee;
  cursor: pointer;
}

.edge-route-card--fullmove {
  border-color: #c7d2fe;
}

.edge-route-card--questions {
  border-color: #99f6e4;
}

.edge-route-card--empty {
  border-style: dashed;
  background: #f8fafc;
}

.edge-route-card--selected {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
}

.edge-route-card--selected.edge-route-card--fullmove {
  border-color: #2563eb;
}

.edge-route-card--selected.edge-route-card--questions {
  border-color: #0d9488;
  background: #f0fdfa;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.18);
}

.edge-route-card__mode {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 9999px;
  margin-bottom: 6px;
}

.edge-route-card[dir="rtl"] .edge-route-card__mode {
  text-transform: none;
  letter-spacing: 0;
}

.edge-route-card--fullmove .edge-route-card__mode {
  background: #eef2ff;
  color: #4338ca;
}

.edge-route-card--questions .edge-route-card__mode {
  background: #ccfbf1;
  color: #0f766e;
}

.edge-route-card__condition {
  font-size: 0.76rem;
  line-height: 1.45;
  color: #334155;
  white-space: normal;
  word-break: normal;
  overflow-wrap: anywhere;
}

.edge-route-card--empty .edge-route-card__condition {
  color: #94a3b8;
  font-style: italic;
}

:deep(.vue-flow__node-default) {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.agent-flow-node {
  min-width: 280px;
  max-width: 340px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #dbe3ee;
  overflow: hidden;
}

.agent-flow-node--root {
  border-color: #a5b4fc;
}

.agent-flow-node__header {
  padding: 10px 14px 0;
}

.agent-flow-node__badge {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 9999px;
  background: #f1f5f9;
  color: #475569;
}

.agent-flow-node__badge--rtl {
  text-transform: none;
  letter-spacing: 0;
}

.agent-flow-node--root .agent-flow-node__badge {
  background: #eef2ff;
  color: #4338ca;
}

.agent-flow-node__body {
  padding: 12px 14px 16px;
}

.agent-flow-node__identity {
  display: flex;
  align-items: flex-start;
  flex-direction: row;
}

.agent-flow-node__identity--rtl {
  flex-direction: row-reverse;
}

.agent-flow-node__avatar {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  margin-inline-end: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-flow-node__identity--rtl .agent-flow-node__avatar {
  margin-inline-end: 0;
  margin-inline-start: 12px;
}

.agent-flow-node--root .agent-flow-node__avatar {
  border-color: #c7d2fe;
  background: #eef2ff;
}

.agent-flow-node__avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-flow-node__text {
  min-width: 0;
  flex: 1;
}

.agent-flow-node__identity--rtl .agent-flow-node__text {
  text-align: right;
}

.agent-flow-node__name {
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.35;
  color: #0f172a;
  word-break: normal;
  overflow-wrap: anywhere;
}

.agent-flow-node__meta {
  margin-top: 6px;
  font-size: 0.74rem;
  line-height: 1.45;
  color: #64748b;
}

:deep(.vue-flow__handle) {
  width: 11px;
  height: 11px;
  border: 2px solid #ffffff;
  background: #64748b;
}

.agent-flow-node--root :deep(.vue-flow__handle) {
  background: #4f46e5;
}

:deep(.vue-flow__node.selected) {
  box-shadow: none;
}

:deep(.vue-flow__node-default.selected) {
  box-shadow: none !important;
}

:deep(.vue-flow__node.selected .agent-flow-node) {
  border: 2px solid #3b82f6;
}

:deep(.vue-flow__edge-path) {
  stroke: #94a3b8;
  stroke-width: 2;
  transition: stroke 0.2s ease, stroke-width 0.2s ease;
}

:deep(.vue-flow__edge:hover .vue-flow__edge-path),
:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  stroke: #2563eb;
  stroke-width: 3;
}

:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  filter: drop-shadow(0 0 2px rgba(37, 99, 235, 0.35));
}

</style>
